import React from 'react';
type CommonProps = {
  variant?: 'primary' | 'secondary' | 'outline';
  children: React.ReactNode;
  className?: string;
};
type AnchorProps = CommonProps & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'className'> & {
  href: string;
};
type ButtonElProps = CommonProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> & {
  href?: undefined;
};
export type ButtonProps = AnchorProps | ButtonElProps;
const baseStyles = 'inline-flex items-center justify-center px-6 py-3 font-semibold rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
const variants = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-600 shadow-sm',
  secondary: 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50 focus:ring-blue-600',
  outline: 'bg-transparent text-white border-2 border-white hover:bg-white/10 focus:ring-white'
};
/**
 * Polymorphic Button — renders an <a> when `href` is provided, otherwise a <button>.
 * This keeps all the visual styling consistent while ensuring navigation links
 * actually navigate.
 */
export function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    children,
    className = ''
  } = props;
  const classes = `${baseStyles} ${variants[variant]} ${className}`;
  if ('href' in props && props.href !== undefined) {
    const {
      href,
      target,
      rel,
      variant: _v,
      children: _c,
      className: _cn,
      ...rest
    } = props as AnchorProps;
    const safeRel = target === '_blank' ? rel ?? 'noopener noreferrer' : rel;
    return <a href={href} target={target} rel={safeRel} className={classes} {...rest} data-id="element-14">
        {children}
      </a>;
  }
  const {
    variant: _v,
    children: _c,
    className: _cn,
    ...rest
  } = props as ButtonElProps;
  return <button className={classes} {...rest} data-id="element-15">
      {children}
    </button>;
}